import * as Sentry from "@sentry/browser";
import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  defaultDataIdFromObject,
  InMemoryCache,
  Operation,
} from "@apollo/client";
import { getMainDefinition } from "@apollo/client/utilities";
import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import { setContext } from "@apollo/client/link/context";
import { isNil } from "ramda";
// QA-IMPERSONATION
import { QA_IMPERSONATION_HEADER, readImpersonation } from "./qaImpersonation";

/**
 * Never retry mutations: the request may have reached the server before the response was lost, and resending could
 * record a payment twice (Cash/Check have no idempotency key).
 */
export const shouldRetryOperation = (error: unknown, operation: Operation): boolean => {
  if (!error) return false;
  const definition = getMainDefinition(operation.query);
  return !(definition.kind === "OperationDefinition" && definition.operation === "mutation");
};

/**
 * How many clients this session has built. Constructing a second one throws away
 * the entire normalized cache, which is what used to take the open inspection
 * dialog down with it (every `useQuery` re-subscribes against an empty cache, and
 * the job page renders `null` when it has no job). The provider now memoizes, so
 * this should stay at 1 for the life of a session; anything higher is a
 * regression and reports itself.
 */
let clientsCreated = 0;

export default function createApolloClient(getToken: any) {
  clientsCreated += 1;
  if (clientsCreated > 1) {
    Sentry.withScope((scope) => {
      scope.setLevel("warning" as Sentry.Severity);
      scope.setTag("area", "apollo");
      scope.setExtra("clientsCreated", clientsCreated);
      Sentry.captureMessage("apollo.client_recreated");
    });
  }

  const httpLink = createHttpLink({
    uri: `${process.env.REACT_APP_BACKEND_GRAPHQL_ENDPOINT}`, // Server URL (must be absolute)
  });
  const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    if (graphQLErrors) {
      Sentry.captureException(new Error("GraphQL Request Error"), {
        extra: {
          errorAsString: graphQLErrors.map(
            ({ message, locations, path }) =>
              `Operation Name: ${
                operation?.operationName
              } -- [GraphQL Error]: Message: ${message}, Locations: ${JSON.stringify(
                locations
              )}, Path: ${path} -- Operation Query: ${JSON.stringify(
                operation?.query
              )} -- Operation Variables ${JSON.stringify(operation?.variables)}`
          ),
        },
      });
    }
    if (networkError) {
      Sentry.captureException(new Error("GraphQL Network Error"), {
        extra: {
          errorAsString: `Operation Name: ${
            operation?.operationName
          } -- [Network error]: ${networkError} -- Operation Query: ${JSON.stringify(
            operation?.query
          )} -- Operation Variables ${JSON.stringify(operation?.variables)}`,
        },
      });
    }
  });
  const retryLink = new RetryLink({ attempts: { retryIf: shouldRetryOperation } });
  const authLink = setContext(async (_, { headers }) => {
    const token = await getToken();
    // QA-IMPERSONATION — read per request, not captured: the client is built once per
    // session, so a selection made later still has to reach the server. The backend
    // ignores this header in production and whenever its own constant is off.
    const impersonated = readImpersonation();
    return {
      headers: {
        ...headers,
        authorization: `Bearer ${token}`,
        "req-sent-from": "techWebApp",
        ...(impersonated ? { [QA_IMPERSONATION_HEADER]: impersonated.id } : {}),
      },
    };
  });
  return new ApolloClient({
    link: ApolloLink.from([errorLink, retryLink, authLink, httpLink]),
    connectToDevTools: true,
    cache: new InMemoryCache({
      possibleTypes: {
        CustomerGetInvoiceResult: ["InvoicePaidResult", "CustomerInvoice"],
        PerformActionResult: ["Contact", "Estimate", "Job", "Invoice"],
      },
      typePolicies: {
        EJIDiscount: {
          keyFields: false,
        },
        /*
         * VehicleInfo has no id, so Apollo stores it inline on its parent rather
         * than normalizing it, and the default behaviour when a query writes one
         * is to REPLACE whatever is already there.
         *
         * Four queries select different subsets of VehicleInfo. getMyAppointments
         * asks for three fields and polls once a minute for the open-job banner;
         * getJob asks for nineteen. Without a merge policy that poll was wiping
         * odometer, licensePlate, CCA and the rest out of the cache every 60s,
         * which changed the inspection form's initialValues and reset what the
         * technician had typed.
         *
         * `merge: true` shallow-merges incoming fields over existing ones, so a
         * narrow query tops up the cached object instead of truncating it.
         */
        VehicleInfo: {
          merge: true,
        },
      },
      dataIdFromObject: (object) => {
        if (object.__typename === "Technician") {
          /*
           * Custom dataIdFromObject for Technician
           *
           * Our Appointments resolver resolves the technician field to the
           * technicianCopy stored alongside that Appointment and this copy is not updated.
           *
           * This actually makes some sense, because it can be useful to have the tech info
           * at the time of creation of that appointment (for example the market he was in)
           *
           * However, Apollo Client is not meant to be used this way. It trusts our backend to always
           * return the newest data, even when it's nested, and it's going to use the first return to
           * set the cache.
           *
           * This is problematic when we change the market a tech is in and particularly, the home store.
           *
           * This is a way to keep using the cache as much as we can but acknowledging that there
           * may be difference between home parts stores for the same technician (same id)
           *
           */
          const homePartsStoreId =
            !isNil(object.homePartsStore) && typeof object.homePartsStore === "object"
              ? (object.homePartsStore as any)?.id
              : undefined;
          if (!isNil(object.id) && !isNil(homePartsStoreId)) {
            return `${object.__typename}:${object.id}:HomePartsStore:${(object.homePartsStore as any).id}`;
          }
        }
        return defaultDataIdFromObject(object);
      },
    }),
  });
}
